(ns ycweb.app.website.auth.confirm
  "The confirm link emailed when someone registers with the email of an account
   that has no password yet (auth.api.activation): they enter the password they
   chose again, and are logged in."
  (:require
   [rum.core :as rum]
   [ycweb.app.website.auth.common :as auth.cmn]
   [ycweb.app.website.website :as website]
   [ycweb.components.appcommon :as appcommon]
   [ycweb.components.common :as cmn]
   [ycweb.components.flow :as flow]
   [ycweb.components.forms :as forms]
   [ycweb.components.grid :as grid]
   [ycweb.components.headings :as headings]
   [ycweb.components.toaster :as toaster]
   [ycweb.components.transitions :as t]
   [ycweb.util.comms :as comms]
   [ycweb.util.core :as u]
   [ycweb.util.hash :as hash]
   [ycweb.util.urlstate :as urlstate]))

(defn- confirm-cursor [app-atom]
  (rum/cursor-in (cmn/process-cursor app-atom) [:confirm-account]))

(defn- submit! [app-atom]
  (let [confirm-atom (confirm-cursor app-atom)
        password (get-in @confirm-atom [:user/password :value])
        {:keys [captcha-site-key]} (:config @app-atom)]
    (cond
      (not (u/presence password))
      (swap! confirm-atom assoc :error "Please enter the password you chose.")

      :else
      (do
        (swap! confirm-atom dissoc :error)
        (toaster/open-overlay app-atom :web.login/confirm-account)
        (appcommon/recaptcha-then captcha-site-key
          (fn [captcha-token]
            (comms/ajax-post :web.auth/confirm-account
              {:token (:t (cmn/screen-params app-atom))
               :pwd (hash/md5 password)
               :captcha-token captcha-token
               :session/php-session-id (get @app-atom :php-session-id)}))
          ;; No token: the overlay above would otherwise never close
          (fn []
            (toaster/close-overlay app-atom :web.login/confirm-account)
            (toaster/open-toast app-atom :error appcommon/recaptcha-failure-msg
              {:show-close-button true})))))))

;; ========== CONFIRM: ENTER THE PASSWORD AGAIN ==========
(rum/defc confirm-account-modal-body < rum/reactive [app-atom _ _]
  (let [confirm-atom (confirm-cursor app-atom)
        error (cmn/curact confirm-atom [:error])]
    [:<>
     [:div.modal__header
      (headings/heading "Confirm it's you" {:type :h2 :size :default :flush true :class "modal__heading"})]
     [:div.modal__content
      [:p.yc-paragraph "Enter the password you chose when you registered to finish setting up your account."]
      (forms/password-input "Password:" "Enter your password"
        (rum/cursor-in confirm-atom [:user/password])
        {:required? true
         :yc-input? true
         :on-key-down
         (fn [e _]
           (when (== (.-keyCode e) 13)
             (.preventDefault e)
             (submit! app-atom)))})
      (when error
        [:p {:class (u/bem :yc-paragraph :u-text-red)} error])]
     (toaster/loading-overlay app-atom :web.login/confirm-account)]))

(rum/defc confirm-account-modal-footer < rum/reactive [app-atom _ _]
  [:div
   (grid/button-row
     {:single-text "Confirm"
      :single-fn #(submit! app-atom)})])

(defmethod grid/modal-config :web.login/confirm-account [_ _ _]
  {:body-raw confirm-account-modal-body
   :footer confirm-account-modal-footer
   :modal-type :variable
   :cancel-key :auth-modal-close})

;; ========== CONFIRM: LINK NO LONGER WORKS ==========
;; Expired, spent or already active: the message and where its button goes
;; (Create account again, which resends the email, or log in) come from the
;; response, via the modal config
(def ^:private link-modals
  {:web.login/confirm-expired {:heading "Link expired" :button "Resend verification email"}
   :web.login/confirm-already-active {:heading "Your account is ready" :button "Log in"}})

(rum/defc confirm-link-modal-body < rum/reactive [_app-atom config-atom _]
  (let [{:keys [heading msg]} (rum/react config-atom)]
    [:<>
     [:div.modal__header
      (headings/heading heading {:type :h2 :size :default :flush true :class "modal__heading"})]
     [:div.modal__content
      [:p.yc-paragraph msg]]]))

(rum/defc confirm-link-modal-footer < rum/reactive [app-atom config-atom _]
  (let [{:keys [button payload]} (rum/react config-atom)]
    [:div
     (grid/button-row
       {:single-text button
        :single-fn #(urlstate/raw-redirect app-atom payload)})]))

(def ^:private confirm-link-modal
  {:body-raw confirm-link-modal-body
   :footer confirm-link-modal-footer
   :modal-type :variable
   :cancel-key :auth-modal-close})

(defmethod grid/modal-config :web.login/confirm-expired [_ _ _] confirm-link-modal)
(defmethod grid/modal-config :web.login/confirm-already-active [_ _ _] confirm-link-modal)

(defmethod comms/receive :web.auth/confirm-account [app-atom {:keys [action msg payload]}]
  (toaster/close-overlay app-atom :web.login/confirm-account)
  (cond
    (= action :redirect)
    (grid/dispatch-action app-atom :redirect {:payload payload})

    (= action :web.login/confirm-wrong-password)
    (swap! (confirm-cursor app-atom) assoc :error msg)

    (contains? link-modals action)
    (do
      (grid/close-modal app-atom)
      (grid/open-modal app-atom action
        (merge (get link-modals action)
          {:msg msg
           :payload payload
           :config
           {:size :small
            :blur-bg? true}})))))

(defmethod flow/render-screen :web.login/confirm [app-atom _]
  (website/screen-wrapper app-atom
    (auth.cmn/auth-landing-page app-atom "confirm")
    {}))

(defmethod t/transition-open :web.login/confirm [app-atom _ _]
  (grid/open-modal app-atom :web.login/confirm-account
    {:config
     {:size :small
      :blur-bg? true}}))
